import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTable, useFilters, usePagination } from "react-table";
import { Badge, Button } from "react-bootstrap";
import swal from "sweetalert";

import { ResidenteService } from "services/ResidenteService";
import { getDefaultParams } from "services/DefaultParamets";
import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { LinkPrimary } from "jsx/components/CitoPlus/components/CitoPlusLink";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

import "../../../table/FilteringTable/filtering.css";

const PreRegistrosList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  const handleDelete = async (slug, nombre) => {
    const willDelete = await swal({
      title: "¿Estás seguro?",
      text: `¿Deseas desactivar el pre-registro de ${nombre}?`,
      icon: "warning",
      buttons: ["Cancelar", "Sí, desactivar"],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        await ResidenteService.deletePreRegistro(slug);
        notifySuccess("Pre-registro desactivado correctamente");
        fetchUrl(currentPage, searchValue);
      } catch (error) {
        notifyError("Error al desactivar el pre-registro");
        handleErrors(error);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "created_at",
        Header: "Fecha Creación",
        accessor: "created_at",
        disableFilters: true,
        disableSortable: false,
      },
      {
        id: "nombres",
        Header: "Nombres",
        accessor: "nombres",
        disableFilters: true,
      },
      {
        id: "cedula",
        Header: "Cédula",
        accessor: "cedula",
        disableFilters: true,
      },
      {
        id: "fecha_inicio",
        Header: "Fecha Inicio",
        accessor: "fecha_inicio",
        disableFilters: true,
        disableSortable: false,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "fecha_final",
        Header: "Fecha Final",
        accessor: "fecha_final",
        disableFilters: true,
        disableSortable: false,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "placa",
        Header: "Placa",
        accessor: "placa",
        disableFilters: true,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "is_active",
        Header: "Estado",
        accessor: "is_active",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) =>
          value ? (
            <Badge bg="success">Activo</Badge>
          ) : (
            <Badge bg="secondary">Inactivo</Badge>
          ),
      },
      {
        id: "actions",
        Header: "Acciones",
        disableFilters: true,
        disableSortable: true,
        accessor: (row) => {
          return (
            <div className="d-flex gap-2">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDelete(row.slug, row.nombres)}
                disabled={!row.is_active}
                title="Desactivar pre-registro"
              >
                <i className="fas fa-trash-alt"></i>
              </Button>
            </div>
          );
        },
      },
    ],
    [currentPage, searchValue],
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedfetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [],
  );

  useEffect(() => {
    fetchUrl(1);
  }, []);

  const fetchUrl = async (pageIndex = 1, search = "", ordering = "") => {
    setLoading(true);
    try {
      const params = {
        ...defaultParams,
        page: pageIndex,
        search: search || searchValue,
        ordering: ordering,
      };

      const response = await ResidenteService.getPreRegistros(params);

      setNextUrl(response.data.next);
      setPreviousUrl(response.data.previous);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(response.data.current_page || pageIndex);
      setData(response.data.results || []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedfetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    setSearchValue("");
    fetchUrl(1, "");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";

    setSortField(id);
    setOrder(newOrder);
    setLoading(true);
    fetchUrl(1, searchValue, `${newOrder === "desc" ? "-" : ""}${id}`);
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    usePagination,
  );

  const { page } = tableInstance;

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Mis Pre-registros</h4>
        <LinkPrimary to="/pre-registro-crear">
          <i className="fas fa-plus me-2"></i>
          Nuevo Pre-registro
        </LinkPrimary>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-tools">
            <SearchInput
              value={searchValue}
              onSearchChange={handleSearchChange}
              onEnterPress={handleEnterPress}
              onClearClick={handleClearClick}
            />
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <SpinnerCitoPlus text="Cargando pre-registros..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-clipboard-list fa-3x text-muted mb-3"></i>
              <h5>No hay pre-registros</h5>
              <p className="text-muted">
                Crea un pre-registro para agilizar el ingreso de tus visitantes.
              </p>
              <LinkPrimary to="/pre-registro-crear" className="mt-2">
                <i className="fas fa-plus me-2"></i>
                Crear Pre-registro
              </LinkPrimary>
            </div>
          ) : (
            <Tables
              columns={columns}
              data={data}
              loading={loading}
              totalPages={totalPages}
              currentPage={currentPage}
              fetchUrl={fetchUrl}
              handleSortingChange={handleSortingChange}
              nextUrl={nextUrl}
              previousUrl={previousUrl}
              sortField={sortField}
              order={order}
              page={page}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PreRegistrosList;
